import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

export type WindowBasketItem = {
  id: string;
  windowType: string;
  windowStyle: number;
  frameColor: string;
  glassType: string;
  clearGlassType?: 'float' | 'toughened' | 'laminated';
  obscuredGlassType?: string;
  quantity: number;
  price: number;
  createdAt: number;
  updatedAt: number;
  imagePath?: string; // Store the primary preview image path (usually external)
  // Optional additional previews for Window Generator-based designs
  imagePathInternal?: string; // Internal view (presentation or generic)
  imagePresentationOutside?: string;
  imagePresentationInside?: string;
  imageDesignOutside?: string;
  imageDesignInside?: string;
  imageBaySchematic?: string;
  windowGeneratorPayload?: any;
  // Additional specifications
  width?: number;
  height?: number;
  profileDepth?: number;
  profileType?: 'Sculptured Profile' | 'Chamfered Profile';
  beadingType?: 'Sculptured (Ovolo)' | 'Chamfered';
  cillRequired?: boolean;
  cillIncluded?: boolean;
  cillHeight?: number;
  cillType?: 'Regular' | 'Stub' | '';
  trickleVents?: boolean; // Whether trickle vents are required
  notes?: string;
  reference?: string; // Window reference for this specific window
  bayTangentWidthMm?: number;
  bayLegacyWidthMm?: number;
  bayProjectionDepthMm?: number;
  bayFacetWidthsMm?: string;
  bayJointAnglesDeg?: string;
};

type WindowBasketState = {
  items: WindowBasketItem[];
  customerReference: string;
  addItem: (item: Omit<WindowBasketItem, 'id' | 'createdAt' | 'updatedAt'>) => string;
  removeItem: (id: string) => void;
  updateItem: (id: string, updates: Partial<Omit<WindowBasketItem, 'id' | 'createdAt' | 'updatedAt'>>) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearItems: () => void;
  getTotalPrice: () => number;
  getItemCount: () => number;
  setCustomerReference: (reference: string) => void;
};

export const useWindowBasketStore = create<WindowBasketState>()(
  persist(
    (set, get) => ({
      items: [],
      customerReference: '',
      
      addItem: (item) => {
        const id = uuidv4();
        const timestamp = Date.now();
        
        set((state) => ({
          items: [
            ...state.items,
            {
              ...item,
              id,
              createdAt: timestamp,
              updatedAt: timestamp
            }
          ]
        }));
        
        return id;
      },
      
      removeItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id)
        }));
      },
      
      updateItem: (id, updates) => {
        set((state) => ({
          items: state.items.map((item) => 
            item.id === id 
              ? { ...item, ...updates, updatedAt: Date.now() } 
              : item
          )
        }));
      },
      
      updateQuantity: (id, quantity) => {
        if (quantity < 1) return;
        
        set((state) => ({
          items: state.items.map((item) => 
            item.id === id 
              ? { ...item, quantity, updatedAt: Date.now() } 
              : item
          )
        }));
      },
      
      clearItems: () => {
        set({ items: [], customerReference: '' });
      },
      
      getTotalPrice: () => {
        return get().items.reduce((total, item) => {
          return total + (item.price * item.quantity);
        }, 0);
      },
      
      getItemCount: () => {
        return get().items.reduce((count, item) => count + item.quantity, 0);
      },
      
      setCustomerReference: (reference) => {
        set((state) => ({
          ...state,
          customerReference: reference
        }));
      }
    }),
    {
      name: 'window-basket'
    }
  )
); 